import { Upload } from 'antd';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { RenderElementProps, useFocused, useSelected } from 'slate-react';
import { useUploadContext } from './UploadContext';
import { css, cx } from '@emotion/css';
import { getVideoThumbUrl } from 'utils/validator/video';
import { UploadFile } from 'antd/es/upload/interface';
import { HTMLAttributes } from 'react';
import { UploadType } from 'components/TemplateEditor/slateTypes';
import ProxyVideo from 'components/ProxyVideo';
import { isURL } from 'utils/is';

function Element(props: RenderElementProps) {
  const { attributes, children, element } = props;

  const { imageList, videoList } = useUploadContext();

  switch (element.type) {
    case CustomSlateNodeType.UPLOAD: {
      const { uploadType, id } = element;

      const file: UploadFile | undefined = isURL(id)
        ? {
            uid: id,
            name: id,
            thumbUrl: id,
            status: 'done',
          }
        : {
            IMAGE: imageList,
            VIDEO: videoList,
          }[uploadType]?.find(({ uid }) => uid === id);

      if (!file) {
        return <></>;
      }

      return (
        <UploadElement {...attributes} className={className} file={file} uploadType={uploadType}>
          {children}
        </UploadElement>
      );
    }
    default:
      return <div {...attributes}>{children}</div>;
  }
}

export function UploadElement({
  file,
  uploadType,
  children,
  className,
  ...otherProps
}: HTMLAttributes<HTMLSpanElement> & {
  file: UploadFile;
  uploadType: UploadType;
}) {
  const { imageModal, videoModal } = useUploadContext();

  const focused = useFocused();
  const selected = useSelected();

  return (
    <span {...otherProps} className={cx(className, focused && selected && selectedClassName)}>
      <div contentEditable={false} onClick={(e) => e.stopPropagation()}>
        <Upload
          listType="picture-card"
          fileList={[file]}
          previewFile={
            uploadType === 'VIDEO'
              ? (file) => {
                  const videoUrl = URL.createObjectURL(file);
                  return getVideoThumbUrl(videoUrl)
                    .catch(() => '')
                    .then((thumbUrl) => {
                      return thumbUrl;
                    })
                    .finally(() => {
                      URL.revokeObjectURL(videoUrl);
                    });
                }
              : undefined
          }
          iconRender={
            uploadType === 'VIDEO'
              ? (file) => {
                  return (
                    <ProxyVideo src={file.thumbUrl}>
                      <track kind="captions" />
                    </ProxyVideo>
                  );
                }
              : undefined
          }
          onPreview={(file) => {
            const { thumbUrl, response: { url = thumbUrl } = {} } = file;

            if (uploadType === 'IMAGE') {
              imageModal.show(url);
            }

            if (uploadType === 'VIDEO') {
              videoModal.show(url);
            }
          }}
        />
      </div>
      {children}
    </span>
  );
}

const className = css`
  display: inline-block;
  margin: 0 8px 8px 0;
  .ant-upload-list-picture-card-container {
    width: 60px;
    height: 60px;
    margin: 0;
    .ant-upload-list-item {
      padding: 4px;
    }
    .ant-upload-list-item-actions button {
      display: none;
    }
    .ant-upload-list-item-thumbnail {
      font-size: 10px;
      video {
        height: 100%;
      }
    }
    .ant-upload-list-item-name {
      display: none;
    }
  }
`;

const selectedClassName = css`
  box-shadow: 0 0 0 3px #b4d5ff;
`;

export default Element;
